import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { t } from '../../../../services/i18nService';

import stringUtils from '../../../../qti/utils/StringUtils';

import KeyboardHelpAndPrompt from '../../keyboardHelp/KeyboardHelpAndPrompt';
import Gap from './Gap';
import GapChoicesContainer from './GapChoicesContainer';
import GapMatchPaperBased from './GapMatchPaperBased';

import styles from './gapMatchInteraction.scss';
import LoadTracker from '../../../../services/LoadTracker';
import sessionReader from '../../../../store/readHelpers/SessionReader';
import MathDisplay from '../../mathDisplay/MathDisplay';
import { showMedia } from '../../media/MediaDisplay';
import uiService from '../../../../services/UIService';
import scorableItemNumberingService from '../../../../services/questionView/ScorableItemNumberingService';
import QuestionDisplayFooter from '../../QuestionDisplayFooter';
import questionReader from '../../../../store/readHelpers/QuestionReader';
import GapMatchInteractionServices from '../../../../services/questionView/GapMatchInteractionServices';

class GapMatchInteraction extends React.Component {
    static propTypes = {
        callback: PropTypes.func.isRequired, // callback(responseIdentifier, [simpleChoice identifiers])
        handleInteractionReady: PropTypes.func.isRequired,
        model: PropTypes.object.isRequired, // model taken from responseModels related to this interaction
        userResponse: PropTypes.array.isRequired, // array with simpleChoice identifiers
        services: PropTypes.object,
        settingValues: PropTypes.object,
        isReadOnly: PropTypes.bool,
        isPreRender: PropTypes.bool,
        questionModel: PropTypes.object,
        direction: PropTypes.string,
        zoomFactor: PropTypes.string,
    };

    constructor(props) {
        super(props);
        this.state = Object.assign({
            canvasId: stringUtils.getUID(),
            gapWidth: undefined,
            gapHeight: undefined,
        }, this.getMaps(this.props));

        this.setReadyTracker();
    }

    componentDidMount() {
        const {
            isPreRender,
            questionModel,
            model: { displayContext, mediaModels },
            services,
        } = this.props;
        if (this.gapChoicesContainer) {
            MathDisplay
                .showMath(this.gapChoicesContainer.rootElement)
                .then(() => this.setGapDimensions());
        }

        const showMediaParams = {
            displayContext,
            isInteraction: true,
            isPreRender,
            questionObjectType: questionModel.objectType,
            services,
        };

        if (Array.isArray(mediaModels)) {
            mediaModels.forEach((mediaModel) => {
                showMedia({ ...showMediaParams, model: mediaModel });
            });
        }
    }

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillReceiveProps(newProps) {
        this.setState(
            this.getMaps(newProps)
        );
    }

    componentDidUpdate() {
        if (!sessionReader.isPrintStylePaperBased()) {
            this.injectGaps();
        }
        this.readyTracker.setAllItemsAdded();
    }

    setReadyTracker() {
        const { identifier } = this.props.model;
        this.readyTracker = new LoadTracker(this.props.handleInteractionReady, `GapMatchInteraction ${identifier} > Gaps and Gap Choices`);
        this.reportGapChoicesContainerReady = this.readyTracker.addItemToTrack(`GapMatchInteraction ${identifier} > GapTextsContainer`);
        this.reportGapsReady = this.readyTracker.addItemToTrack(`GapMatchInteraction ${identifier} > Gaps`);
    }

    getMaps = (newProps) => {
        const { model, userResponse, correctResponses } = newProps;
        const maps = {
            droppedGapChoices: [],
            notDropped: [],
            gapMap: {},
            gapChoiceMap: {},
            gapTextToGapMap: {}, // Key = gapText.identifier, value = gap.identifier
            gapToGapTextMap: {}, // Key = gap.identifier, value = gapText.identifier
        };
        const inResponse = userResponse.map(item => item.split(' ')[0]);
        let responses = userResponse;
        if (!userResponse?.length && correctResponses?.length) {
            responses = correctResponses;
        }
        responses.forEach((item) => {
            const match = item.split(' ');
            if (maps.gapTextToGapMap[match[0]]) {
                maps.gapTextToGapMap[match[0]].push(match[1]);
            } else {
                maps.gapTextToGapMap[match[0]] = [match[1]];
            }
            if (maps.gapToGapTextMap[match[1]]) {
                maps.gapToGapTextMap[match[1]].push(match[0]);
            } else {
                maps.gapToGapTextMap[match[1]] = [match[0]];
            }
        });

        model.gapModels.forEach((gapModel) => {
            maps.gapMap[gapModel.identifier] = gapModel;
        });

        model.gapChoiceModels.forEach((gapChoiceModel) => {
            maps.gapChoiceMap[gapChoiceModel.identifier] = gapChoiceModel;

            if (inResponse.indexOf(gapChoiceModel.identifier) !== -1) {
                maps.droppedGapChoices.push(gapChoiceModel);
            } else {
                maps.notDropped.push(gapChoiceModel);
            }
        });

        return maps;
    };

    setGapDimensions = async () => {
        const { model, isPreRender, questionModel } = this.props;
        if (!this.gapChoicesContainer || isPreRender) {
            return;
        }

        const isMultipleInteractionsGapMatchQuestion = questionReader.isGapMatchQuestionWithMultipleInteractions(questionModel);
        const gapTextElements = await this.gapChoicesContainer.getGapTextElements();
        let gapWidth = isMultipleInteractionsGapMatchQuestion ? window.innerWidth : 0;
        let gapHeight = 0;
        const maximumGapWidthAllowed = 0.3 * window.innerWidth;
        gapTextElements.forEach((element) => {
            const displayValue = element.style.display;
            element.style.display = 'inline-flex';
            if (!isMultipleInteractionsGapMatchQuestion) {
                gapWidth = element.clientWidth > gapWidth ? element.clientWidth : gapWidth;
                gapHeight = element.clientHeight > gapHeight ? element.clientHeight : gapHeight;
            } else {
                gapWidth = Math.min(gapWidth, element.clientWidth, maximumGapWidthAllowed);
            }
            element.style.display = displayValue;
        });
        model.gapChoiceModels.forEach((choiceModel) => {
            if (choiceModel.type === 'gapImg') {
                const { height, width } = choiceModel.dimensions;
                gapWidth = width > gapWidth ? width : gapWidth;
                gapHeight = height > gapHeight ? height : gapHeight;
            }
        });
        this.setState({ gapWidth, gapHeight });
    };

    /**
     * Returns a new user response array, with any matches with gapTextIdentifier with a gap identifier other than
     * gapIdentifier removed.
     * @param {Array} userResponse
     * @param {string} gapIdentifier Gap match to keep
     * @return {Array}
     */
    removeGapPairByGapIdentifier = (userResponse, gapIdentifier) => {
        return userResponse.filter((item) => {
            const pair = item.split(' ');
            return pair[1] !== gapIdentifier;
        });
    };

    removeGapPairFromResponse = (userResponse, pair) => {
        return userResponse.filter((item) => {
            return item !== pair;
        });
    };

    handleUserResponse = (gapTextIdentifier, nextGapIdentifier, prevGapIdentifier, modelIdentifier) => {
        const { model, userResponse, questionModel } = this.props;
        const isMultipleInteractionGapMatchQuestion = questionReader.isGapMatchQuestionWithMultipleInteractions(questionModel);
        if (isMultipleInteractionGapMatchQuestion && nextGapIdentifier && modelIdentifier) {
            const gapModelIdentifier = model.gapModels.find(gapModel => gapModel.identifier === nextGapIdentifier);
            if (gapModelIdentifier && model.identifier !== modelIdentifier) { // invalid case
                return;
            }
        }
        const nextPair = `${gapTextIdentifier} ${nextGapIdentifier}`;
        let newResponse = userResponse.slice();
        if (prevGapIdentifier && nextGapIdentifier !== prevGapIdentifier) {
            const prevPair = `${gapTextIdentifier} ${prevGapIdentifier}`;
            newResponse = this.removeGapPairFromResponse(userResponse, prevPair);
        }

        if (nextGapIdentifier && newResponse.indexOf(nextPair) === -1) {
            newResponse = this.removeGapPairByGapIdentifier(newResponse, nextGapIdentifier);
            newResponse.push(nextPair);
        }

        this.props.callback(model.identifier, newResponse);
    };

    getGap = (gapModel, tracker) => {
        const { model, questionModel, services } = this.props;
        const { gapToGapTextMap } = this.state;
        const reportReady = tracker.addItemToTrack(`Gap ${gapModel.identifier}`);
        const gapTextIdentifiers = gapToGapTextMap[gapModel.identifier] || [];
        const gapChoiceModels = model.gapChoiceModels.filter(gapChoiceModel => gapTextIdentifiers.indexOf(gapChoiceModel.identifier) > -1);
        const gapNumber = services.scorableItemNumberingService.getInteractionChoiceNumber(
            questionModel.contentRevisionId, model.identifier, gapModel.identifier
        );
        const isActive = services.scorableItemNumberingService.isActiveScorableItem(gapNumber);
        const isFlagged = services.scorableItemNumberingService.isFlaggedScorableItem(`${questionModel.contentRevisionId}-${gapNumber}`);
        const extendedRenderingActivated = model.extendedRenderingActivated;
        return (
            <Gap
                {...this.props}
                {...this.state}
                gapModel={gapModel}
                gapChoiceModels={gapChoiceModels}
                handleUserResponse={this.handleUserResponse}
                key={gapModel.identifier}
                reportReady={reportReady}
                orderNumber={gapNumber}
                isActive={isActive}
                isFlagged={isFlagged}
                questionModel={questionModel}
                extendedRenderingActivated={extendedRenderingActivated}
            />
        );
    };

    injectGaps = () => {
        const { model } = this.props;
        const gapsTracker = new LoadTracker(this.reportGapsReady, `GapMatchInteraction ${model.identifier} > injectGaps`);

        model.gapModels.forEach((gapModel) => {
            ReactDOM.render(
                this.getGap(gapModel, gapsTracker),
                document.getElementById(gapModel.placeholderId)
            );
        });

        gapsTracker.setAllItemsAdded();
    };

    setGapChoicesContainerRef = (instance) => {
        this.gapChoicesContainer = instance;
    };

    getGapMatchContent = () => {
        const { model, questionModel, zoomFactor } = this.props;
        const { notDropped } = this.state;
        const tokenPosition = model.tokenPosition || 'top';
        const isReversedPosition = ['bottom', 'right', 'rightWithSplitPreview'].includes(tokenPosition);

        const classList = [styles.container];
        const printStyle = sessionReader.getPrintParam('printStyle');
        const multiInteractionsClass = GapMatchInteractionServices
            .getMultiInteractionClass(tokenPosition, zoomFactor, model.shouldAutoSizeTokens);

        if (model.extendedRenderingActivated) {
            if (printStyle) {
                classList.push(styles.extendedRenderingPrintStyle);
            } else {
                classList.push(styles.extendedRendering);
            }
        }

        if (multiInteractionsClass.length > 0) {
            classList.push(multiInteractionsClass);
        }

        const choicesContainer = (<GapChoicesContainer
            {...this.props}
            {...this.state}
            model={model}
            notDropped={notDropped}
            handleUserResponse={this.handleUserResponse}
            reportReady={this.reportGapChoicesContainerReady}
            ref={this.setGapChoicesContainerRef}
        />);

        const interactionsContainer = (<div
            ref={(canvas) => { this.canvas = canvas; }}
            className={classList.join(' ')}
            id={`interactionsContainer-${model.identifier}`}
            {...uiService.getQuestionRegionsAccessibilityAttributes()}
        >
            { uiService.areQuestionSectionsFocusable() &&
                <span className="sr-only">
                    { `${t('generic.responseAreaForPrefix')} ${scorableItemNumberingService.getQuestionTitle(questionModel.contentRevisionId)}` }
                </span>
            }

            <div dangerouslySetInnerHTML={{ __html: GapMatchInteractionServices.getInteractionBody(model.interactionBody) }} />
        </div>);

        if (isReversedPosition) {
            return (<React.Fragment>
                { interactionsContainer }
                { choicesContainer }
            </React.Fragment>);
        }

        return (<React.Fragment>
            { choicesContainer }
            { interactionsContainer }
        </React.Fragment>);
    };

    render() {
        const { model, questionModel, direction, services = {}, settingValues = {} } = this.props;
        const isMultipleInteractionsGapMatchQuestion = questionReader.isGapMatchQuestionWithMultipleInteractions(questionModel);
        const tokenPosition = model.tokenPosition || 'top';

        if (sessionReader.isPrintStylePaperBased()) {
            return (
                <GapMatchPaperBased
                    {...this.props}
                    {...this.state}
                    reportGapsReady={this.reportGapsReady}
                    reportGapChoicesContainerReady={this.reportGapChoicesContainerReady}
                    gapChoicesContainerRef={this.setGapChoicesContainerRef}
                />
            );
        }
        return (
            <div
                className="interaction skipMathCache allowed-break-inside"
                id={this.state.uid}
                dir={direction}
            >
                <KeyboardHelpAndPrompt
                    captionKey="interactions.gapMatch.keyboardInteractionHelpText"
                    translationService={services.translationService}
                    model={model}
                    showAccessibilityHelpers={settingValues.show_accessibility_helpers}
                    questionModel={questionModel}
                />
                <div
                    className={`${styles.wrapper} tokens-${tokenPosition}`}
                >
                    { this.getGapMatchContent() }
                </div>
                {
                    !model?.hideFooter && isMultipleInteractionsGapMatchQuestion &&
                    <QuestionDisplayFooter
                        isLastQuestionInCurrentNestedSection={false}
                        question={this.props.questionModel}
                        setUserResponse={this.handleUserResponse}
                    />
                }
            </div>
        );
    }
}

export default GapMatchInteraction;
